import {
  FlaskConical, Globe2, Cog, Lightbulb, PawPrint, CloudSun, Building2, HeartPulse, Rocket, Wheat, BrainCircuit, Zap, Tornado,
  Footprints, Fish, Scale,
} from "lucide-react";

export const slug = (s) => s.toLowerCase().replace(/&/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Four MUCCI learning pathways. Concepts are listed in the order they fan out around the hub.
export const PATHWAYS = [
  { id: "science", title: "Science Inquiry", lines: ["Science Inquiry"], icon: FlaskConical, fill: "#D6F5EA", stroke: "#4FB48A",
    concepts: ["Life Science", "Earth & Space Science", "Physical Science"] },
  { id: "social", title: "Social Studies & Humanities", lines: ["Social Studies", "& Humanities"], icon: Globe2, fill: "#FDDFE4", stroke: "#E2899B",
    concepts: ["Economics", "Civics", "History", "Geography", "Culture"] },
  { id: "engineering", title: "Engineering & Design", lines: ["Engineering", "& Design"], icon: Cog, fill: "#D7EAFE", stroke: "#6AA4E3",
    concepts: ["Design Process", "Programming", "Robotics", "Electronics", "Mechanics"] },
  { id: "critical", title: "Critical Thinking & Global Issues", lines: ["Critical Thinking", "& Global Issues"], icon: Lightbulb, fill: "#ECE3FE", stroke: "#A386E3",
    concepts: ["Evidence", "Reasoning", "Multiple Perspectives", "Argumentation", "Ethics"] },
];

// Interdisciplinary real-world themes. Each draws on specific concepts from two to four pathways.
// Listed in the order they sit around the central ring (clockwise from the right).
export const PROJECTS = [
  { id: "migration", title: "Migration & Cultural Identity", icon: Footprints,
    links: ["Culture", "History", "Geography", "Civics", "Multiple Perspectives", "Argumentation"] },
  { id: "rights", title: "Technology & Human Rights", icon: Scale,
    links: ["Programming", "Electronics", "Civics", "History", "Ethics", "Argumentation", "Multiple Perspectives"] },
  { id: "ai", title: "Artificial Intelligence & Society", icon: BrainCircuit,
    links: ["Programming", "Robotics", "Electronics", "Civics", "Economics", "Ethics", "Argumentation"] },
  { id: "energy", title: "Energy & Sustainability", icon: Zap,
    links: ["Physical Science", "Electronics", "Design Process", "Economics", "Civics", "Evidence"] },
  { id: "cities", title: "Sustainable Cities", icon: Building2,
    links: ["Physical Science", "Design Process", "Mechanics", "Electronics", "Geography", "Civics", "Ethics"] },
  { id: "disasters", title: "Natural Disasters", icon: Tornado,
    links: ["Earth & Space Science", "Mechanics", "Design Process", "Geography", "History", "Evidence"] },
  { id: "space", title: "Space Exploration", icon: Rocket,
    links: ["Earth & Space Science", "Physical Science", "Mechanics", "Robotics", "Programming", "History", "Reasoning"] },
  { id: "climate", title: "Climate Change", icon: CloudSun,
    links: ["Earth & Space Science", "Physical Science", "Economics", "Programming", "Evidence", "Multiple Perspectives"] },
  { id: "endangered", title: "Endangered Species", icon: PawPrint,
    links: ["Life Science", "Earth & Space Science", "Geography", "Economics", "Evidence", "Ethics"] },
  { id: "water", title: "Water & Food Security", icon: Wheat,
    links: ["Life Science", "Earth & Space Science", "Design Process", "Geography", "Economics", "Ethics"] },
  { id: "oceans", title: "Oceans & Plastic Pollution", icon: Fish,
    links: ["Life Science", "Physical Science", "Design Process", "Economics", "Civics", "Evidence"] },
  { id: "health", title: "Human Health", icon: HeartPulse,
    links: ["Life Science", "History", "Culture", "Programming", "Robotics", "Reasoning", "Ethics"] },
];

// Related concepts across pathways (these also "spark" one at a time once the map has grown)
export const CROSS_LINKS = [
  ["Physical Science", "Mechanics"], ["Earth & Space Science", "Geography"], ["Programming", "Reasoning"],
  ["Economics", "Evidence"], ["Life Science", "Ethics"], ["History", "Multiple Perspectives"],
  ["Physical Science", "Electronics"], ["Civics", "Argumentation"], ["Design Process", "Culture"],
];

// wide: full map with knowledge concepts and a central ring of themes
// tall (mobile): simplified map — pathway hubs and themes only; concepts are listed in the panel
const LAYOUTS = {
  wide: {
    w: 1100, h: 800, spread: 150, r: 140, stagger: 26, hubR: 40, nodeR: 8, projR: 21, font: 13, hubFont: 15, icon: 30,
    hubs: { science: [255, 200], social: [845, 200], engineering: [255, 585], critical: [845, 585] },
    out: { science: 235, social: 305, engineering: 125, critical: 55 },
    ring: { cx: 550, cy: 390, rx: 228, ry: 172, start: 15 },
  },
  tall: {
    w: 600, h: 1040, hubR: 46, projR: 28, font: 18, hubFont: 20, icon: 34, simple: true,
    hubs: { science: [120, 130], social: [480, 130], engineering: [120, 910], critical: [480, 910] },
    grid: { xs: [110, 300, 490], ys: [290, 438, 586, 734] },
  },
};

const wrap = (label) => {
  if (label.length <= 12 || !label.includes(" ")) return [label];
  const words = label.split(" ");
  let best = [label];
  let diff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(" "), b = words.slice(i).join(" ");
    if (Math.abs(a.length - b.length) < diff) { diff = Math.abs(a.length - b.length); best = [a, b]; }
  }
  return best;
};

// Greedy word wrap for theme labels (keeps neighbouring labels on the ring from overlapping)
const wrapLines = (label, max) =>
  label.split(" ").reduce((lines, word) => {
    const last = lines[lines.length - 1];
    if (last && `${last} ${word}`.length <= max) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
    return lines;
  }, []);

// Place a concept label on the outer side of its node, kept inside the canvas
const placeLabel = (label, x, y, dx, dy, L, gap = L.nodeR + 8) => {
  const lines = wrap(label);
  const lh = L.font * 1.15;
  const estW = Math.max(...lines.map((l) => l.length)) * L.font * 0.56;
  const anchor = dx > 0.35 ? "start" : dx < -0.35 ? "end" : "middle";
  let lx = x + dx * gap;
  let ly = y + dy * gap;
  if (anchor === "start") lx = Math.min(lx, L.w - 4 - estW);
  if (anchor === "end") lx = Math.max(lx, 4 + estW);
  if (anchor === "middle") lx = Math.min(Math.max(lx, estW / 2 + 4), L.w - estW / 2 - 4);
  const y0 = anchor === "middle"
    ? (dy < 0 ? ly - (lines.length - 1) * lh - 2 : ly + L.font * 0.6)
    : ly - ((lines.length - 1) * lh) / 2;
  return { lines, anchor, dx: lx - x, dy: y0 - y, lh };
};

const PHI = 2.399963;

export const buildGraph = (mode) => {
  const L = LAYOUTS[mode];
  const nodes = [];
  const edges = [];
  let n = 0;
  const phase = () => (n++ * PHI) % (Math.PI * 2);

  PATHWAYS.forEach((p, i) => {
    const [hx, hy] = L.hubs[p.id];
    const top = hy < L.h / 2;
    const lh = L.hubFont * 1.15;
    nodes.push({
      id: p.id, kind: "hub", pathway: p.id, x: hx, y: hy, r: L.hubR, amp: 2.5, phase: phase(), start: 0.2 + i * 0.25,
      label: { lines: p.lines, anchor: "middle", dx: 0, dy: top ? L.hubR + L.hubFont + 6 : -L.hubR - 12 - (p.lines.length - 1) * lh, lh },
    });
    const count = p.concepts.length;
    if (L.simple) return;
    p.concepts.forEach((c, j) => {
      const deg = L.out[p.id] - L.spread / 2 + (count === 1 ? L.spread / 2 : (L.spread * j) / (count - 1));
      const a = (deg * Math.PI) / 180;
      const r = L.r + (j % 2 ? L.stagger : 0);
      const dx = Math.cos(a), dy = Math.sin(a);
      const x = hx + dx * r, y = hy + dy * r;
      const id = slug(c);
      const start = 1.0 + i * 0.25 + j * 0.15;
      edges.push({ id: `b:${id}`, kind: "branch", a: p.id, b: id, curv: j % 2 ? 0.16 : -0.16, color: p.stroke, start, dur: 0.9, phase: phase() });
      nodes.push({ id, kind: "concept", title: c, pathway: p.id, x, y, r: L.nodeR, amp: 4, phase: phase(), start: start + 0.7, label: placeLabel(c, x, y, dx, dy, L) });
    });
  });

  if (!L.simple) {
    CROSS_LINKS.forEach(([a, b], k) => {
      const pa = PATHWAYS.find((p) => p.concepts.includes(a));
      edges.push({ id: `x:${slug(a)}-${slug(b)}`, kind: "cross", a: slug(a), b: slug(b), curv: k % 2 ? 0.22 : -0.22, color: pa.stroke, start: 3.0 + k * 0.15, dur: 1.4, phase: phase(), k });
    });
  }

  PROJECTS.forEach((p, i) => {
    let x, y, label;
    const lh = L.font * 1.15;
    if (L.simple) {
      x = L.grid.xs[i % 3]; y = L.grid.ys[Math.floor(i / 3)];
      label = { lines: wrapLines(p.title, 13), anchor: "middle", dx: 0, dy: L.projR + L.font * 0.9, lh };
    } else {
      const a = ((L.ring.start + i * (360 / PROJECTS.length)) * Math.PI) / 180;
      x = L.ring.cx + Math.cos(a) * L.ring.rx; y = L.ring.cy + Math.sin(a) * L.ring.ry;
      label = { lines: wrapLines(p.title, 12), anchor: "middle", dx: 0, dy: L.projR + L.font * 0.95, lh };
    }
    const start = 3.4 + i * 0.45;
    const pathways = PATHWAYS.filter((pw) => pw.concepts.some((c) => p.links.includes(c))).map((pw) => pw.id);
    nodes.push({ id: p.id, kind: "project", title: p.title, icon: p.icon, pathways, x, y, r: L.projR, amp: 3, phase: phase(), start, label, i });
    // mobile: one link per pathway; desktop: one link per specific concept
    const targets = L.simple
      ? pathways.map((id) => ({ id, color: pathwayOf(id).stroke }))
      : p.links.map((c) => ({ id: slug(c), color: PATHWAYS.find((q) => q.concepts.includes(c)).stroke }));
    targets.forEach((tg, j) => {
      edges.push({ id: `p:${p.id}-${tg.id}`, kind: "project", a: p.id, b: tg.id, curv: j % 2 ? 0.12 : -0.12, color: tg.color, start: start + 0.3 + j * 0.05, dur: 1.1, phase: phase(), j });
    });
  });

  const byId = Object.fromEntries(nodes.map((nd) => [nd.id, nd]));
  return { L, nodes, edges, byId };
};

export const pathwayOf = (id) => PATHWAYS.find((p) => p.id === id);
